/* AddEditUserRoleComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./AddEditUserRoleRenderer");

/**
 * @class AddEditImageServerComponent
 * @extends EventEmitter
 *
 * @fires AddEditImageServerComponent#Data
 * @fires AddEditImageServerComponent#Error
 * @fires AddEditImageServerComponent#Shutdown
 */

// AddEditUserRoleComponent
class AddEditUserRoleComponent extends EventEmitter {
    constructor(data) {
        super();
        this._data = data;
        this._actionGroupEntityDef = this._data.entityDef;
        this._activeOrgId = this._data.activeOrgId;
        this._actionGroupEntity = _.get(
            this._data,
            "actionGroupEntity",
            new this._actionGroupEntityDef(this._activeOrgId)
        );

        this._descendantOrgs = this._data.descendantOrgs;

        this._isSeeded = false;
        this._isInherited = false;
        this._categories = [];
        if (_.get(this._actionGroupEntity, "seeded", false)) {
            this._isSeeded = true;
        }
        if (this._actionGroupEntity.getTenantId() !== this._activeOrgId) {
            this._isInherited = true;
        }

        if (_.get(this._data, "manageRoleComponent._canEdit", false)) {
            this._isEditable = true;
        }

        this._userFeedback = this._data.userFeedback;
        this._customFieldsWithInherited = this._data.customFieldsWithInherited;
        this._deviceEntity = this._data.deviceEntity;
        this._manageRoleComponent = this._data.manageRoleComponent;
        this._siteEntity = this._manageRoleComponent._SiteEntityClass;
        this._sitePaths = this._manageRoleComponent._sitePaths;
        this._vendorMap = this._manageRoleComponent._vendorMap;
        this._typeBaseMap = this._manageRoleComponent._typeBaseMap;
        this._copyFrom = this._data.copyFrom;
        this._uiUtils = this._data.uiUtils;
        this._compareActionGroup = undefined;
        this._rolesArray = undefined;
        this._copyActionGroup = undefined;
        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer.getRootElement();
    }
    get activeOrgId() {
        return this._activeOrgId;
    }

    get userFeedback() {
        return this._userFeedback;
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/
    _triggerData(data) {
        /**
         * @event AddEditImageServerComponent#Data
         * @param {Object} - entity properties.
         */
        this.emit("Data", data);
    }

    _triggerError(error) {
        /**
         * @event AddEditImageServerComponent#Error
         * @param {*} - entity properties.
         */
        this.emit("Error", error);
    }

    _registerListeners() {
        this._actionGroupEntity.on("DirtyStateChanged", this.dirtyStateChanged.bind(this));
        this._manageRoleComponent.on("OrganizationChanged", this._onOrganizationChanged.bind(this));
    }

    _unregisterListeners() {
        this._actionGroupEntity.removeAllListeners("DirtyStateChanged");
        this._manageRoleComponent.removeListener(
            "OrganizationChanged",
            this._onOrganizationChanged
        );
    }

    _shutdown() {
        if (_.isUndefined(this._renderer)) {
            return Promise.resolve();
        }

        this._unregisterListeners();

        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = undefined;
                this._isSeeded = undefined;
                this._actionGroupEntityDef = undefined;
                this._actionGroupEntity = undefined;
                this._activeOrgId = undefined;
                this._sitePaths = undefined;
                this._siteEntity = undefined;
                this._manageRoleComponent = undefined;
                this._copyFrom = undefined;
                this._shutdownProm = undefined;
                this._data = undefined;
                this._userFeedback = undefined;
                this._rolesArray = undefined;
                this._copyActionGroup = undefined;
                this._compareActionGroup = undefined;
                this._customFieldsWithInherited = undefined;
                this._deviceEntity = undefined;
                this.removeAllListeners();
                this._isInherited = undefined;
                this._descendantOrgs = undefined;
                this._uiUtils = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    saveActionGroup() {
        return Promise.resolve().then(() => {
            if (this._actionGroupEntity && this._actionGroupEntity.isDirty()) {
                return this._actionGroupEntity.update();
            }
        });
    }

    _retrieveActionGroup(actionGroupId) {
        return this._manageRoleComponent._retrieveActionGroup(actionGroupId);
    }

    getRoles() {
        return this._manageRoleComponent.getRoles().then((roles) => {
            this._rolesArray = roles;
            return Promise.resolve();
        });
    }

    _getPermissionDataArr(actionGroupEntity) {
        // Permissions for current user
        const availableRolesForCurrentUser = _.get(
            this._manageRoleComponent._myUserInfo,
            "authorizedActions",
            []
        );

        const superUser = _.get(this._manageRoleComponent._myUserInfo, "superuser", false);

        let availableRolesInComparedActionGroup;
        if (this._compareActionGroup) {
            availableRolesInComparedActionGroup = Object.keys(
                this._compareActionGroup.getActions()
            ).filter((action) => this._compareActionGroup.getActions()[action] === true);
        }
        const authsObj = actionGroupEntity.getActionAuths();
        let newRole = false;
        let availableRoles = [];
        if (_.isEmpty(actionGroupEntity.getActions())) {
            newRole = true;
        } else {
            // Array containing all available roles
            availableRoles = Object.keys(actionGroupEntity.getActions()).filter(
                (action) => actionGroupEntity.getActions()[action] === true
            );
        }

        let actionName = "";
        let permissionDataArr = [];
        this._categories = [];
        _.forEach(Object.keys(authsObj), (auth) => {
            // Get headings
            if (authsObj[auth].category !== actionName) {
                actionName = authsObj[auth].category;

                const _cat =
                    authsObj[auth].category === undefined
                        ? authsObj[auth].description
                        : authsObj[auth].category;

                this._categories.push(_cat.replaceAll(" ", "").toLowerCase());

                permissionDataArr.push({
                    category: _cat,
                    description:
                        authsObj[auth].category === undefined
                            ? authsObj[auth].description
                            : authsObj[auth].category,
                    name:
                        authsObj[auth].category === undefined
                            ? authsObj[auth].description
                            : authsObj[auth].category,
                    title: true,
                    id: `${auth}-1`,
                });
            }

            if (this._compareActionGroup) {
                permissionDataArr.push({
                    category: authsObj[auth].category,
                    description: `${authsObj[auth].description}`,
                    name: auth,
                    newrole:
                        newRole === true
                            ? false
                            : _.indexOf(availableRoles, auth) === -1
                            ? false
                            : true,
                    compare:
                        _.indexOf(availableRolesInComparedActionGroup, auth) === -1 ? false : true,
                    available:
                        _.indexOf(availableRolesForCurrentUser, auth) === -1 &&
                        !_.get(authsObj[auth], "availableToAll", false) &&
                        !superUser
                            ? false
                            : true,
                    match: true, // HC
                    title: false,
                    id: `${auth}-2`,
                });
            } else {
                permissionDataArr.push({
                    category: authsObj[auth].category,
                    description: `${authsObj[auth].description}`,
                    name: auth,
                    newrole:
                        newRole === true
                            ? false
                            : _.indexOf(availableRoles, auth) === -1
                            ? false
                            : true,
                    available:
                        _.indexOf(availableRolesForCurrentUser, auth) === -1 &&
                        !_.get(authsObj[auth], "availableToAll", false) &&
                        !superUser
                            ? false
                            : true,
                    title: false,
                    id: `${auth}-3`,
                });
            }
        });
        const res = (this._catCounts = this._getCategoryCounts(permissionDataArr));
        this._setCatStatus(permissionDataArr, res);

        return permissionDataArr;
    }

    _getCategoryCounts(permissionDataArr) {
        /*
            Role - editable
                perm - available - total++ , allperms ++
                    newrole - count ++
                perm - not available - allperms ++
                    newrole - readonlyCount ++
            Role - not editable
                perm - available - total++, allperms ++
                    newrole - count ++
                perm - not available - allperms ++
                    newrole - readonlyCount ++
        */
        let res = {};
        permissionDataArr.forEach((item) => {
            const cat = item.category.replace(/ /gi, "");
            if (item.title && !res[item.title]) {
                res[cat] = {
                    count: 0, // count of permissions checked under a category
                    total: 0, // count of available permissions under a category
                    allPermCount: 0, // count of all permissions under a category
                    readOnlyCount: 0, // count of un-available permissions checked under a category
                };
            } else {
                res[cat].allPermCount += 1;
                if (item.available) {
                    res[cat].total += 1;
                    if (item.newrole) {
                        res[cat].count += 1;
                    }
                } else {
                    if (item.newrole) {
                        res[cat].readOnlyCount += 1;
                    }
                }
            }
        });
        return res;
    }

    _setCatStatus(permissionDataArr, res) {
        permissionDataArr.forEach((item) => {
            if (item.title) {
                //  key = item.category.replace(/ /ig, "");
                const key = item.category.replace(/ /gi, "");
                let statusClass = "none-selected";

                if (
                    res[key].count === res[key].total ||
                    res[key].readOnlyCount + res[key].count === res[key].allPermCount
                ) {
                    item.notAllSelected = false;
                    statusClass = "all-selected";
                }
                if (
                    res[key].count < res[key].total ||
                    res[key].readOnlyCount + res[key].count < res[key].allPermCount
                ) {
                    statusClass = "some-selected";
                    item.notAllSelected = true;
                }
                if (res[key].readOnlyCount + res[key].count === 0) {
                    statusClass = "none-selected";
                }
                item.newrole = statusClass;
            }
        });
    }

    _getEmailDataArr(actionGroupEntity) {
        const emailsObj = actionGroupEntity.getNotifyEmailTypes();
        const availNotifEmailObj = actionGroupEntity.getNotifEmails();
        let availableNotifxEmailsTypesInComparedGroup, availableNotifEmailInComparedGroupObj;
        if (this._compareActionGroup) {
            availableNotifEmailInComparedGroupObj = this._compareActionGroup.getNotifEmails();
            availableNotifxEmailsTypesInComparedGroup =
                typeof availableNotifEmailInComparedGroupObj === "object"
                    ? Object.keys(availableNotifEmailInComparedGroupObj)
                    : [];
        }

        const availableNotifxEmailsTypes =
            typeof availNotifEmailObj === "object" ? Object.keys(availNotifEmailObj) : [];

        let emailActionName = "";
        let emailDataArr = [];
        _.forEach(Object.keys(emailsObj), (emailType) => {
            // Get headings
            if (
                emailsObj[emailType].category !== emailActionName &&
                emailsObj[emailType].category !== "All"
            ) {
                emailActionName = emailsObj[emailType].category;

                emailDataArr.push({
                    category: emailsObj[emailType].category,
                    description: emailsObj[emailType].category,
                    name: emailsObj[emailType].category,
                    title: true,
                    id: `${emailType}-1`,
                });
            }

            if (this._compareActionGroup) {
                emailDataArr.push({
                    id: `${emailType}-2`,
                    category: emailsObj[emailType].category,
                    description: `${emailsObj[emailType].name}`,
                    name: emailType,
                    emailAddresses:
                        _.indexOf(availableNotifxEmailsTypes, emailType) !== -1
                            ? availNotifEmailObj[emailType]
                            : "",
                    compare:
                        _.indexOf(availableNotifxEmailsTypesInComparedGroup, emailType) !== -1
                            ? availableNotifEmailInComparedGroupObj[emailType]
                            : "",
                    match: true, // HC
                    title: false,
                });
            } else {
                emailDataArr.push({
                    id: `${emailType}-3`,
                    category: emailsObj[emailType].category,
                    description: `${emailsObj[emailType].name}`,
                    name: emailType,
                    emailAddresses:
                        _.indexOf(availableNotifxEmailsTypes, emailType) !== -1
                            ? availNotifEmailObj[emailType]
                            : "",
                    title: false,
                });
            }
        });

        return emailDataArr;
    }

    getOrgVis() {
        return this._actionGroupEntity.getOrgVis();
    }

    setOrgVis(newVal) {
        this._actionGroupEntity.setOrgVis(newVal);
    }

    getVisibleOrgsList() {
        return this._actionGroupEntity.getVisibleOrgs();
    }

    setVisibleOrgsList(newVal) {
        this._actionGroupEntity.setVisibleOrgs(newVal);
    }

    convertPermissionsDataToSlickFormat(actionGroupEntity) {
        return Promise.resolve()
            .then(() => {
                // Permission array for the actionGroup to be compared

                const permissionDataArr = this._getPermissionDataArr(actionGroupEntity);

                const emailDataArr = this._getEmailDataArr(actionGroupEntity);

                return {
                    permissionsData: permissionDataArr,
                    emailData: emailDataArr,
                };
            })
            .catch((err) => {
                console.log("convertPermissionsDataToSlickFormat", err);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.shutdown().then(() => {
            this.rejectPopUp({userRejected: true});
        });
    }

    resolvePopup(data) {
        this._context.resolve(data);
    }

    rejectPopUp(data) {
        this._context.reject(data);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    dirtyStateChanged() {
        const dirtyState = this._actionGroupEntity.isDirty();
        this.emit("DirtyStateChanged", dirtyState);
    }

    _onOrganizationChanged(orgObj) {
        if (this.activeOrgId === orgObj.id) {
            return;
        }
        this._activeOrgId = orgObj.id;

        this.closePopUp();
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = AddEditUserRoleComponent;
